import React, { useEffect } from 'react';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';

const CookiePolicyPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <style>{`
                .cookie-section {
                    opacity: 0;
                    animation: fadeIn 0.5s ease forwards;
                }
                
                @keyframes fadeIn {
                    to {
                        opacity: 1;
                    }
                }
                
                .section-title {
                    position: relative;
                    display: inline-block;
                }
                
                .section-title::after {
                    content: '';
                    position: absolute;
                    bottom: -5px;
                    left: 0;
                    width: 0;
                    height: 2px;
                    background: linear-gradient(to right, #16a34a, #22c55e);
                    animation: expandLine 0.8s ease forwards;
                    animation-delay: 0.3s;
                }
                
                @keyframes expandLine {
                    to {
                        width: 100%;
                    }
                }
                
                .cookie-type-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .cookie-type-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                }
            `}</style>
            
            <div style={styles.pageContainer}>
                <Header />
                
                <div style={styles.heroSection}>
                    <div style={styles.heroContent}>
                        <h1 style={styles.heroTitle}>Cookie Policy</h1>
                        <p style={styles.heroSubtitle}>
                            How we use cookies and similar technologies on our website
                        </p>
                    </div>
                </div>

                <div style={styles.container}>
                    <div style={styles.content}>
                        <section style={styles.section} className="cookie-section">
                            <p style={styles.lastUpdated}>Last Updated: January 2025</p>
                            
                            <h2 style={styles.sectionTitle} className="section-title">
                                What Are Cookies?
                            </h2>
                            <p style={styles.paragraph}>
                                Cookies are small text files that are placed on your device when you visit our website. 
                                They help us provide you with a better experience by remembering your preferences, 
                                analyzing site traffic, and enabling certain functionalities.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                How We Use Cookies
                            </h2>
                            <p style={styles.paragraph}>
                                WinNest uses cookies and similar technologies for the following purposes:
                            </p>
                            <ul style={styles.bulletList}>
                                <li><strong>Essential Operations:</strong> To enable basic website functionality and security</li>
                                <li><strong>Performance Analysis:</strong> To understand how visitors interact with our website</li>
                                <li><strong>User Experience:</strong> To remember your preferences and settings</li>
                                <li><strong>Marketing:</strong> To deliver relevant content and measure advertising effectiveness</li>
                            </ul>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Types of Cookies We Use
                            </h2>
                            
                            <div style={styles.cookieGrid}>
                                <div style={styles.cookieTypeCard} className="cookie-type-card">
                                    <h3 style={styles.cookieTypeTitle}>Strictly Necessary Cookies</h3>
                                    <p style={styles.cookieTypeDesc}>
                                        These cookies are essential for the website to function properly. They enable basic 
                                        functions like page navigation, secure area access, and protection against spam.
                                    </p>
                                    <p style={styles.cookieDuration}><strong>Duration:</strong> Session</p>
                                </div>

                                <div style={styles.cookieTypeCard} className="cookie-type-card">
                                    <h3 style={styles.cookieTypeTitle}>Analytical Cookies</h3>
                                    <p style={styles.cookieTypeDesc}>
                                        These cookies help us understand how visitors interact with our website by collecting 
                                        and reporting information anonymously. We use Google Analytics for this purpose.
                                    </p>
                                    <p style={styles.cookieDuration}><strong>Duration:</strong> Up to 2 years</p>
                                </div>

                                <div style={styles.cookieTypeCard} className="cookie-type-card">
                                    <h3 style={styles.cookieTypeTitle}>Functional Cookies</h3>
                                    <p style={styles.cookieTypeDesc}>
                                        These cookies enable enhanced functionality and personalization, such as remembering 
                                        your language preference or region.
                                    </p>
                                    <p style={styles.cookieDuration}><strong>Duration:</strong> Up to 1 year</p>
                                </div>

                                <div style={styles.cookieTypeCard} className="cookie-type-card">
                                    <h3 style={styles.cookieTypeTitle}>Marketing Cookies</h3>
                                    <p style={styles.cookieTypeDesc}>
                                        These cookies track your online activity to help advertisers deliver more relevant 
                                        advertising or limit how many times you see an ad.
                                    </p>
                                    <p style={styles.cookieDuration}><strong>Duration:</strong> Up to 1 year</p>
                                </div>
                            </div>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Third-Party Cookies
                            </h2>
                            <p style={styles.paragraph}>
                                We may use third-party services that set cookies on our behalf to provide their services. 
                                These include:
                            </p>
                            <ul style={styles.bulletList}>
                                <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
                                <li><strong>Social Media Platforms:</strong> For social media integration and sharing features</li>
                                <li><strong>Payment Processors:</strong> For secure transaction processing</li>
                            </ul>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Managing Cookies
                            </h2>
                            <p style={styles.paragraph}>
                                You have the right to accept or reject cookies. Most web browsers automatically accept cookies, 
                                but you can modify your browser settings to decline cookies if you prefer. Here's how to manage 
                                cookies in popular browsers:
                            </p>
                            <ul style={styles.bulletList}>
                                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                                <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
                            </ul>
                            
                            <p style={styles.warningBox}>
                                <strong>Please Note:</strong> If you disable cookies, some features of our website may not function properly, 
                                and your user experience may be affected.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Do Not Track
                            </h2>
                            <p style={styles.paragraph}>
                                Some browsers have a "Do Not Track" feature that lets you tell websites that you do not want 
                                your online activities tracked. We currently do not respond to Do Not Track signals, but we 
                                limit tracking to the purposes described in this policy.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Updates to This Policy
                            </h2>
                            <p style={styles.paragraph}>
                                We may update this Cookie Policy from time to time to reflect changes in our practices or 
                                applicable laws. We will notify you of any material changes by posting the new policy on 
                                this page with an updated "Last Updated" date.
                            </p>

                            <h2 style={styles.sectionTitle} className="section-title">
                                Contact Us
                            </h2>
                            <p style={styles.paragraph}>
                                If you have questions about our use of cookies or this Cookie Policy, please contact us:
                            </p>
                            <div style={styles.contactBox}>
                                <p><strong>WinNest Financial Services Private Limited</strong></p>
                                <p>Email: privacy@wealthnest.in</p>
                                <p>Phone: +91 90360 24568</p>
                                <p>Address: #123, 4th Cross, Jayanagar, Bengaluru - 560041</p>
                            </div>
                        </section>
                    </div>
                </div>
                
                <Footer />
            </div>
        </>
    );
};

const styles = {
    pageContainer: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f9fafb',
    },
    heroSection: {
        background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
        padding: '100px 20px 60px',
        marginTop: '60px',
    },
    heroContent: {
        maxWidth: '1200px',
        margin: '0 auto',
        textAlign: 'center',
        color: 'white',
    },
    heroTitle: {
        fontSize: 'clamp(2rem, 5vw, 3rem)',
        fontWeight: '700',
        marginBottom: '1rem',
    },
    heroSubtitle: {
        fontSize: 'clamp(1rem, 2vw, 1.25rem)',
        opacity: 0.95,
    },
    container: {
        flex: 1,
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 20px',
        width: '100%',
    },
    content: {
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        padding: '40px',
    },
    section: {
        marginBottom: '20px',
    },
    lastUpdated: {
        fontSize: '0.875rem',
        color: '#6b7280',
        fontStyle: 'italic',
        marginBottom: '2rem',
    },
    sectionTitle: {
        fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
        fontWeight: '600',
        color: '#1f2937',
        marginTop: '2rem',
        marginBottom: '1rem',
    },
    paragraph: {
        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
        lineHeight: '1.8',
        color: '#4b5563',
        marginBottom: '1rem',
        textAlign: 'justify',
    },
    bulletList: {
        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
        lineHeight: '1.8',
        color: '#4b5563',
        marginLeft: '2rem',
        marginBottom: '1rem',
    },
    cookieGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginTop: '20px',
        marginBottom: '30px',
    },
    cookieTypeCard: {
        backgroundColor: '#f9fafb',
        padding: '20px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
    },
    cookieTypeTitle: {
        fontSize: '1.1rem',
        fontWeight: '600',
        color: '#16a34a',
        marginBottom: '10px',
    },
    cookieTypeDesc: {
        fontSize: '0.9rem',
        color: '#4b5563',
        lineHeight: '1.6',
        marginBottom: '10px',
    },
    cookieDuration: {
        fontSize: '0.85rem',
        color: '#6b7280',
    },
    warningBox: {
        backgroundColor: '#fef3c7',
        border: '1px solid #fbbf24',
        borderRadius: '8px',
        padding: '15px',
        marginTop: '20px',
        marginBottom: '20px',
        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
        color: '#92400e',
    },
    contactBox: {
        backgroundColor: '#f3f4f6',
        padding: '20px',
        borderRadius: '8px',
        marginTop: '1rem',
        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
        lineHeight: '1.8',
        color: '#4b5563',
    },
};

export default CookiePolicyPage;